<template>
  <div class="floating-alert">
    <div class="alert-box">
      <div class="wrapper-alert">
        <div class="alert-header">
          <img class="error-icon" :src="getAssetImageUrl('common/icons/error-icon.svg')" alt="" />
          <p class="accent error">{{ errorTitle }}</p>
        </div>

        <p v-if="errorPaymentMessage" class="small">
          {{ errorPaymentMessage }}
        </p>
        <p v-else class="small">
          <i18n-t keypath="error_credit_low_message" scope="global">
            <template #percent>
              <span class="link-small">{{ creditPercentage }}%</span>
            </template>
          </i18n-t>
        </p>
      </div>

      <!-- 버튼 -->
      <button v-if="!isInApp" class="button-error s-btn" @click="handleUpgrade">
        {{ buttonLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGtag } from '@/composables/shared/useGtag';
import { usePayment } from '@/composables/shared/usePayment';
import { usePaymentStore } from '@/stores/payment';
import { useSubscriptionStore } from '@/stores/subscription';
import { ScenarioUpsell } from '@/types/components';
import { PAYMENT_ERROR_MESSAGE_KEY } from '@/utils/constants/payment';
import { isInCapacitorApp } from '@/utils/device';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();
const paymentStore = usePaymentStore();
const isInApp = isInCapacitorApp();

const { openPlanUpgradePopup, openCreditAutoRechargePopup, openCardManagePopup } = usePayment();
const { trackEventFunnel } = useGtag();

const creditPercentage = computed<number>(() => {
  if (subscriptionStore.purchasedCredits <= 0) return 0;

  const percentage = Math.round((subscriptionStore.currentCredits / subscriptionStore.purchasedCredits) * 100);
  return Math.min(100, Math.max(0, percentage));
});

const alertType = computed<ScenarioUpsell | ''>(() => {
  if (subscriptionStore.lastPaymentErrorCode) {
    return 'CARD_CHANGE';
  }

  if (['FREE', 'STARTER'].includes(subscriptionStore.currentPlan)) {
    return 'PLAN_UPGRADE';
  }

  if (subscriptionStore.currentPlan === 'PRO') {
    return subscriptionStore.currentPlanDetails?.rechargeAllow ? 'REFILL_INCREASE' : 'REFILL_ADD';
  }

  return '';
});

const errorTitle = computed<string>(() => {
  if (alertType.value === 'CARD_CHANGE') {
    return t('error_card_title');
  }
  return subscriptionStore.currentCredits === 0 ? t('error_credit_empty_title') : t('error_credit_low_title');
});

const errorPaymentMessage = computed<string>(() => {
  if (!subscriptionStore.lastPaymentErrorCode) {
    return '';
  }

  const message = PAYMENT_ERROR_MESSAGE_KEY[subscriptionStore.lastPaymentErrorCode] ?? '';
  return message ? t(message) : '';
});

const buttonLabel = computed<string>(() => {
  const text = {
    PLAN_UPGRADE: 'upgrade',
    REFILL_ADD: 'add_extra_payment',
    REFILL_INCREASE: 'change_extra_payment',
    CARD_CHANGE: 'change_payment',
  }[alertType.value];

  return text ? t(text) : '';
});

const handleUpgrade = () => {
  trackEventFunnel('click_credit_alert_button');

  switch (alertType.value) {
    case 'PLAN_UPGRADE':
      openPlanUpgradePopup();
      break;
    case 'REFILL_ADD':
    case 'REFILL_INCREASE':
      openCreditAutoRechargePopup();
      break;
    case 'CARD_CHANGE':
      openCardManagePopup('MANAGE', subscriptionStore.paymentType);
      break;
  }
};

onMounted(() => {
  paymentStore.fetchUserCardList();
});
</script>

<style scoped lang="scss">
.floating-alert {
  position: relative;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 5rem;
  z-index: $z-base;
  .alert-box {
    background: $color-red-lighter;
    border-radius: 8px;
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    &.primary {
      background: $primary-bg;
    }
    p {
      &.small {
        display: none;
      }
    }
  }

  @media (max-width: 1540px) {
    padding: 1rem 2.5rem;
  }
  @include respond-to('mobile-landscape') {
    padding: 1rem 1.25rem;
    .alert-box {
      gap: 0.5rem;
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

.wrapper-alert {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.alert-header {
  display: flex;
  align-items: center;
  gap: $size-s;
  .error-icon {
    width: 1.125rem;
    height: 1.15rem;
    object-fit: contain;
  }
}
</style>
